import {
    type ComponentPropsWithoutRef,
    type ForwardRefExoticComponent,
    type MouseEvent,
    type ReactNode,
    type RefAttributes,
    forwardRef,
} from "react";

import { CloseOutlined } from "@ant-design/icons";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { cva } from "class-variance-authority";

import * as theme from "./Tag.css";

export type TagVariant = keyof typeof theme.variants;

export interface TagProps extends ComponentPropsWithoutRef<"span"> {
    /** Preset from the Figma "Tag / Impero" set. */
    variant?: TagVariant;
    /** 12px glyph before the label (Figma "Icon Start"). */
    icon?: ReactNode;
    /** Shows a close button after the label. */
    closable?: boolean;
    onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
    /** Label for the close button, for screen readers. */
    closeLabel?: string;
    /** Lets a long label wrap onto more lines instead of staying on one. */
    wrap?: boolean;
    /** Dashed border, for a placeholder or an "add" affordance. */
    dashed?: boolean;
    /**
     * Escape hatch for tags coloured from data (risk heat, timeline colour).
     * Each one overrides just the preset colour it names.
     */
    background?: string;
    borderColor?: string;
    textColor?: string;
}

// Ref-forwarding: antd's Popover/Tooltip position themselves off the child node.
export const Tag = forwardRef<HTMLSpanElement, TagProps>(
    (
        {
            variant = "default",
            icon,
            closable = false,
            onClose,
            closeLabel = "Close",
            wrap = false,
            dashed = false,
            background,
            borderColor,
            textColor,
            className,
            children,
            onClick,
            ...props
        },
        ref
    ) => (
        <span
            ref={ref}
            data-slot="tag"
            className={tagStyle({
                variant,
                wrap,
                dashed,
                clickable: Boolean(onClick),
                className,
            })}
            style={colorOverrides({ background, borderColor, textColor })}
            onClick={onClick}
            {...props}
        >
            {icon && (
                <span className={theme.icon} aria-hidden>
                    {icon}
                </span>
            )}
            <span className={theme.label}>{children}</span>
            {closable && (
                <button
                    type="button"
                    aria-label={closeLabel}
                    className={theme.closeButton}
                    // Closing a tag is not clicking it.
                    onClick={(event) => {
                        event.stopPropagation();
                        onClose?.(event);
                    }}
                >
                    <CloseOutlined />
                </button>
            )}
        </span>
    )
    // `common/custom_typings/react.d.ts` retypes forwardRef to a plain function,
    // so this cast is what puts `displayName` back for the apps that include it.
) as ForwardRefExoticComponent<TagProps & RefAttributes<HTMLSpanElement>> & {
    displayName?: string;
};

Tag.displayName = "Tag";

const tagStyle = cva(theme.root, {
    variants: {
        variant: theme.variants,
        clickable: {
            true: theme.clickable,
            false: "",
        },
        wrap: {
            true: theme.wrap,
            false: "",
        },
        dashed: {
            true: theme.dashed,
            false: "",
        },
    },
    compoundVariants: [
        {
            variant: "countPill",
            clickable: true,
            class: theme.countPillClickable,
        },
    ],
});

interface ColorOverrides {
    background?: string;
    borderColor?: string;
    textColor?: string;
}

// Inline vars beat the preset's `fallbackVar` default without any specificity
// games. Returns undefined when nothing is overridden, so the attribute is dropped.
function colorOverrides({
    background,
    borderColor,
    textColor,
}: ColorOverrides) {
    if (!background && !borderColor && !textColor) {
        return undefined;
    }
    return assignInlineVars({
        ...(background && { [theme.tagVars.background]: background }),
        ...(borderColor && { [theme.tagVars.borderColor]: borderColor }),
        ...(textColor && { [theme.tagVars.textColor]: textColor }),
    });
}
